import { List } from "immutable";
import _ from "lodash";

import rectIntersectionFromPoint from "../../algorithms/layout/rectIntersectionFromPoint";
import { immutableRecords } from "../../types";

export const ConnectorLocations = {
  TOP: "TOP",
  LEFT: "LEFT",
  RIGHT: "RIGHT",
  BOTTOM: "BOTTOM",
  TOPRIGHT: "TOPRIGHT",
  BOTTOMRIGHT: "BOTTOMRIGHT",
  TOPLEFT: "TOPLEFT",
  BOTTOMLEFT: "BOTTOMLEFT",
  CENTER: "CENTER"
};

const MIN_LINE_DISTANCE = 20;

const containsLeft = connector => connector === "LEFT" || connector === "TOPLEFT" || connector === "BOTTOMLEFT";
const containsRight = connector => connector === "RIGHT" || connector === "TOPRIGHT" || connector === "BOTTOMRIGHT";
const containsBottom = connector => connector === "BOTTOM" || connector === "BOTTOMLEFT" || connector === "BOTTOMRIGHT";
const containsTop = connector => connector === "TOP" || connector === "TOPLEFT" || connector === "TOPRIGHT";

const onVerticalEdge = connector => containsLeft(connector) || containsRight(connector);
const onHorizontalEdge = connector => containsTop(connector) || containsBottom(connector);
// const onCorner = connector => onVerticalEdge(connector) && onHorizontalEdge(connector);

const onOppositeEdges = (connector1, connector2) =>
  connector1 != connector2 &&
  ((onVerticalEdge(connector1) && onVerticalEdge(connector2)) ||
    (onHorizontalEdge(connector1) && onHorizontalEdge(connector2)));

// Assumes rect has properties:
// - left, right, bottom, top

const coordinatesFromRect = (rect, connector) => {
  let x, y;
  switch (connector) {
    case ConnectorLocations.LEFT:
    case ConnectorLocations.TOPLEFT:
    case ConnectorLocations.BOTTOMLEFT:
      x = rect.left;
      break;
    case ConnectorLocations.RIGHT:
    case ConnectorLocations.TOPRIGHT:
    case ConnectorLocations.BOTTOMRIGHT:
      x = rect.right;
      break;
    default:
      x = (rect.left + rect.right) / 2;
      break;
  }

  switch (connector) {
    case ConnectorLocations.TOP:
    case ConnectorLocations.TOPLEFT:
    case ConnectorLocations.TOPRIGHT:
      y = rect.top;
      break;
    case ConnectorLocations.BOTTOM:
    case ConnectorLocations.BOTTOMLEFT:
    case ConnectorLocations.BOTTOMRIGHT:
      y = rect.bottom;
      break;
    default:
      y = (rect.top + rect.bottom) / 2;
      break;
  }

  return { x, y };
};

const manhattanDistBetweenCoords = (p1, p2) => Math.abs(p1.x - p2.x) + Math.abs(p1.y - p2.y);

const perpendicularLineIntersectsRect = (p1, p2, r) => {
  if (p1.x === p2.x && p1.x >= r.left && p1.x <= r.right) {
    // Vertical line that is within rect in horizontal direction
    return (p1.y < r.bottom && p2.y > r.top) || (p2.y < r.bottom && p1.y > r.top);
  }
  if (p1.y === p2.y && p1.y >= r.top && p1.y <= r.bottom) {
    // Vertical line that is within rect in horizontal direction
    return (p1.x < r.right && p2.x > r.left) || (p2.x < r.right && p1.x > r.left);
  }
  return false;
};

const sameSideConnectorForPoints = (p1, p2, side) => {
  let m1, m2;
  if (onVerticalEdge(side)) {
    const v =
      side === ConnectorLocations.LEFT
        ? Math.min(p1.x, p2.x) - MIN_LINE_DISTANCE
        : Math.max(p1.x, p2.x) + MIN_LINE_DISTANCE;
    m1 = { x: v, y: p1.y };
    m2 = { x: v, y: p2.y };
  } else if (onHorizontalEdge(side)) {
    const h =
      side === ConnectorLocations.TOP
        ? Math.min(p1.y, p2.y) - MIN_LINE_DISTANCE
        : Math.max(p1.y, p2.y) + MIN_LINE_DISTANCE;
    m1 = { x: p1.x, y: h };
    m2 = { x: p2.x, y: h };
  } else {
    return null;
  }

  return [
    immutableRecords.PointRecord(p1),
    immutableRecords.PointRecord(m1),
    immutableRecords.PointRecord(m2),
    immutableRecords.PointRecord(p2)
  ];
};

const oppositeSideConnectorForPoints = (p1, p2, side1) => {
  let m1, m2;
  if (onVerticalEdge(side1)) {
    const v = Math.floor((p1.x + p2.x) / 2);
    m1 = { x: v, y: p1.y };
    m2 = { x: v, y: p2.y };
  } else if (onHorizontalEdge(side1)) {
    const h = Math.floor((p1.y + p2.y) / 2);
    m1 = { x: p1.x, y: h };
    m2 = { x: p2.x, y: h };
  } else {
    return null;
  }

  return [
    immutableRecords.PointRecord(p1),
    immutableRecords.PointRecord(m1),
    immutableRecords.PointRecord(m2),
    immutableRecords.PointRecord(p2)
  ];
};

const adjacentSideConnectorForPoints = (p1, p2, side1) => {
  let m;
  if (onVerticalEdge(side1)) {
    m = { x: p2.x, y: p1.y };
  } else if (onHorizontalEdge(side1)) {
    m = { x: p1.x, y: p2.y };
  } else {
    return null;
  }

  return [immutableRecords.PointRecord(p1), immutableRecords.PointRecord(m), immutableRecords.PointRecord(p2)];
};

const bendyConnectorForPoints = (p1, p2, side1, side2) => {
  if (side1 === side2) {
    return sameSideConnectorForPoints(p1, p2, side1);
  } else if (onOppositeEdges(side1, side2)) {
    return oppositeSideConnectorForPoints(p1, p2, side1);
  } else {
    return adjacentSideConnectorForPoints(p1, p2, side1);
  }
};

const straightConnectorForPoints = (p1, p2) => {
  return [immutableRecords.PointRecord(p1), immutableRecords.PointRecord(p2)];
};

export const bendyConnectorForRects = (rect1, rect2) => {
  const connectorOptions = _.values(ConnectorLocations);
  const PASSTHROUGH_PENALTY = 10000;
  const MIN_DISTANCE_PENALTY = 400;

  let bestScore = null;
  let result = null;

  connectorOptions.forEach(c1 => {
    const p1 = coordinatesFromRect(rect1, c1);

    connectorOptions.forEach(c2 => {
      const p2 = coordinatesFromRect(rect2, c2);

      const dist = manhattanDistBetweenCoords(p1, p2);

      // Score based on how close to the centroid angle we are
      let score = dist;
      const points = bendyConnectorForPoints(p1, p2, c1, c2);

      let intersects = false;
      const last = points.length - 1;

      for (let i = 0; i < last - 1; i++) {
        intersects =
          intersects ||
          perpendicularLineIntersectsRect(points[i], points[i + 1], rect1) ||
          perpendicularLineIntersectsRect(points[i], points[i + 1], rect2);
      }

      const tooShort =
        Math.abs(points[0].x - points[1].x) + Math.abs(points[0].y - points[1].y) < MIN_LINE_DISTANCE ||
        Math.abs(points[last].x - points[last - 1].x) + Math.abs(points[last].y - points[last - 1].y) <
          MIN_LINE_DISTANCE;

      // Add a penalty if the line will pass through this item
      score += intersects ? PASSTHROUGH_PENALTY : 0;
      score += tooShort ? MIN_DISTANCE_PENALTY : 0;

      if (onOppositeEdges(c1, c2)) {
        if (onVerticalEdge(c1)) {
          if (Math.abs(p1.x - p2.x) < MIN_LINE_DISTANCE) {
            score += MIN_DISTANCE_PENALTY;
          }
        } else {
          if (Math.abs(p1.y - p2.y) < MIN_LINE_DISTANCE) {
            score += MIN_DISTANCE_PENALTY;
          }
        }
      }

      if (bestScore === null || score < bestScore) {
        bestScore = score;
        result = points;
      }
    });
  });
  return List(result);
};

// export const rectIntersectionFromPoint = (rect1, p1, p2) => {
//   const SHRINK_AMOUNT = 2;

//   // Test simple edge cases
//   if (p1.x === p2.x) {
//     // On same x-plane. Return that x position and top or bottom
//     if (p1.x < p2.x) {
//       // center of rect is above p2, so line will hit bottom
//       return immutableRecords.PointRecord({x: p1.x, y: rect1.bottom - SHRINK_AMOUNT});
//     } else {
//       // Otherwise, line will hit top.
//       return immutableRecords.PointRecord({x: p1.x, y: rect1.top + SHRINK_AMOUNT});
//     }
//   }
//   if(p1.y === p2.y) {
//     // On same y-plane. Return left or right, and that y position
//     if (p1.y < p2.y) {
//       // center of rect is left of p2, so line will hit right
//       return immutableRecords.PointRecord({x: rect1.right - SHRINK_AMOUNT, y: p1.y});
//     } else {
//       // Otherwise, line will hit bottom.
//       return immutableRecords.PointRecord({x: rect1.left + SHRINK_AMOUNT, y: p1.y});
//     }
//   }

//   // If we reach here, need to do some intersection calcs
//   const m = (p2.y - p1.y) / (p2.x - p1.x);

//   if (p1.x < p2.x) {
//     // rect is to the left, so only need to test right
//     const y = m * (rect1.right - p1.x) + p1.y;
//     if (y >= rect1.top && y <= rect1.bottom) {
//       return immutableRecords.PointRecord({x: rect1.right - SHRINK_AMOUNT, y: y});
//     }
//   } else {
//     // rect is to the right, so only need to test left
//     const y = m * (rect1.left - p1.x) + p1.y;
//     if (y >= rect1.top && y <= rect1.bottom) {
//       return immutableRecords.PointRecord({x: rect1.left + SHRINK_AMOUNT, y: y});
//     }
//   }

//   if (p1.y < p2.y) {
//     // rect is to the top, so only need to test botttom
//     const x = (rect1.bottom - p1.y) / m + p1.x;
//     if (x >= rect1.left && x <= rect1.right) {
//       return immutableRecords.PointRecord({x: x, y: rect1.bottom  - SHRINK_AMOUNT});
//     }
//   } else {
//     // rect is to the bottom, so only need to test top
//     const x = (rect1.top - p1.y) / m + p1.x;
//     if (x >= rect1.left && x <= rect1.right) {
//       return immutableRecords.PointRecord({x: x, y: rect1.top + SHRINK_AMOUNT});
//     }
//   }

//   // Should never reach here, but if we do, use the center
//   return p1;
// };

export const connectorForRects = (rect1, rect2) => {
  // Get the centers of the two rectangles
  const c1 = coordinatesFromRect(rect1, ConnectorLocations.CENTER);
  const c2 = coordinatesFromRect(rect2, ConnectorLocations.CENTER);
  // Now calculate the intended intersection points
  const p1 = rectIntersectionFromPoint(rect1, c1, c2);
  const p2 = rectIntersectionFromPoint(rect2, c2, c1);

  const points = straightConnectorForPoints(p1, p2);

  return List(points);
};
